// ---- Headwear seating (pure, DOM-free) ----
// THE seat math for anything on the head. Prim headwear (iron-helm, coifs,
// hats) AND coded hoods (via wear/seat.ts hoodSeatFromRig, which reuses
// humanoidCrownFrame + scalp landmarks) MUST use this — never freehand apex
// heights. Diagnostics for humanoid `slots.headwear` / wear.part head fillers.
// Parts are authored as offsets from the CROWN anchor (near the top of the
// egg head). Used by inspect-model --head-seat and check-model soft WARNs.
//
// Also reports the hair contract: helmets hide host hair by default;
// crowns set `hidesHair: false` (see headwearHidesHair in index.ts).
//
// Shell vs brim: shellRatio / dome checks use the COVERING dome only. A wide
// sun-hat disc (straw-hat) is tagged `brim` and must NOT drive shell size —
// otherwise every intentional brim falsely WARNs "oversized shell".

import type { Part, Vec3 } from "./parts.ts";
import { BUILD_PRESETS, HUMANOID_TUNING } from "./humanoid.ts";
import type { BodyBuild } from "../shared.ts";

// ---- Hair contract (also used by buildFromDoc / mergeWear) ----

/** Headwear hair contract: helmets/caps/hats hide host hair by default
 *  (omit or `hidesHair: true`). Crowns/circlets set `hidesHair: false` so
 *  hair shows under them. Unknown filler → hide (clipping-safe). */
export function headwearHidesHair(
  filler: { hidesHair?: boolean } | undefined | null,
): boolean {
  if (!filler) return true;
  return filler.hidesHair !== false;
}

// ---- Bands (crown-local; tuned against iron-helm / leather-cap / straw-hat) ----

/** Shell max radius / skull radius: snug metal/cloth shell.
 *  (skull r already includes the 1.1 humanoid base scale; part r is crown-local.) */
export const SHELL_R_MIN = 0.98;
/** Above this the dome reads as a floating pot (pre-fix iron-helm was ~1.35). */
export const SHELL_R_MAX = 1.28;
/** Rim band center local y (relative to crown) — near brow / upper forehead. */
export const RIM_Y_MIN = -0.20;
export const RIM_Y_MAX = -0.08;
/** Dome base y (lathe at[1]) — should sit with the rim, not float at crown. */
export const DOME_BASE_Y_MAX = -0.05;
/** Apex (highest point of shell, excl. plume/tall ornaments) above crown. */
export const APEX_ABOVE_CROWN_MAX = 0.12;
/**
 * Shell apex world-y − skull top. Coverage headwear should cup the egg —
 * iron-helm ≈ 0.06, leather-cap ≈ 0.11, a floating top-hat crown was ~0.15.
 * Independent of the crown-anchor offset (crown sits slightly above skullTop).
 */
export const SKULL_CLEARANCE_MAX = 0.12;
/** Front nasal/peak proud of head surface at brow (world z − surface). */
export const NASAL_PROUD_MAX = 0.08;
/** Worn head AABB width / bare head width soft ceiling. */
export const WORN_WIDTH_RATIO_MAX = 1.18;
/**
 * Lathe height at/under this is "flat" (rim band or sun-hat brim) — excluded
 * from shell-size ranking so a wide brim can't steal shellRatio.
 */
export const SHORT_LATHE_H = 0.07;
/**
 * Short lathe maxR at/above this is a wide brim disc (straw-hat), not a
 * brow band. Bands on helm/cap stay under this (~0.18–0.22).
 */
export const BRIM_R_MIN = 0.24;

export type CrownFrame = {
  /** Crown anchor world (feet-space, rest pose). */
  crown: Vec3;
  headY: number;
  /** Horizontal skull radius (lathe max r × scale). */
  headR: number;
  headDepth: number;
  /** World y of skull top (egg apex). */
  skullTopY: number;
  scale: number;
};

/** Rest-pose crown frame matching humanoid() anchors (incl. the 1.1 base scale). */
export function humanoidCrownFrame(opts?: {
  scale?: number;
  build?: BodyBuild;
}): CrownFrame {
  const T = HUMANOID_TUNING;
  const s = (opts?.scale ?? 1) * 1.1;
  const P = BUILD_PRESETS[opts?.build ?? "average"];
  // Match humanoid.ts hip/shoulder/headY chain (dy for non-average legLen).
  const hipY = 1.06 * s * P.legLen;
  const dy = hipY - 1.06 * s; // shift absolute profiles when legs shorten
  const shoulderY = hipY + 0.24 * s + 0.34 * s;
  const headY = shoulderY + 0.23 * s;
  const headR = 0.155 * s;
  // headProfile top y is 2.045 nominal × s + dy (profAbs).
  const skullTopY = 2.045 * s + dy;
  return {
    crown: [0, headY + 0.19 * s, 0],
    headY,
    headR,
    headDepth: T.headDepth,
    skullTopY,
    scale: s,
  };
}

export type HeadPartSeat = {
  index: number;
  prim: string;
  color: string;
  /** Local to crown. */
  local: Vec3;
  /** Max horizontal radius estimate of this part. */
  maxR: number;
  /** Lowest / highest local y extent. */
  yMin: number;
  yMax: number;
  /** Heuristic: covering dome lathe (skull-scale shell, NOT the sun-hat brim). */
  isShell: boolean;
  /** Heuristic: short brow-band / gold-ring lathe near the base. */
  isRim: boolean;
  /** Heuristic: wide flat sun-hat disc — excluded from shell size. */
  isBrim: boolean;
  /** Heuristic: front nasal/peak box. */
  isNasal: boolean;
  /** Ornament (plume/ball apex) — excluded from shell-size checks. */
  isOrnament: boolean;
};

export type HeadSeatReport = {
  frame: CrownFrame;
  fillerId: string;
  /** Resolved hair contract for this filler. */
  hidesHair: boolean;
  parts: HeadPartSeat[];
  /** Covering-shell max r / headR (null when no shell lathe; never uses brim). */
  shellRatio: number | null;
  rimY: number | null;
  domeBaseY: number | null;
  apexLocalY: number | null;
  /**
   * Shell apex world y − skullTopY. Null when no apex. The "gap above the
   * head" number — lower the dome / shorten the crown until this is small.
   */
  skullClearance: number | null;
  /** Widest brim disc maxR, if any (info only — not a seat fail). */
  brimR: number | null;
  warns: string[];
};

function partCenterLocal(part: Part): Vec3 {
  if (part.prim === "rod" || part.prim === "bone") {
    return [
      (part.from[0] + part.to[0]) / 2,
      (part.from[1] + part.to[1]) / 2,
      (part.from[2] + part.to[2]) / 2,
    ];
  }
  return [...part.at] as Vec3;
}

/** Horizontal shell radius (lathe profile r — depth ovalizes Z only, not width).
 *  Exported for the body-envelope checks in wear-check.ts (same arithmetic). */
export function partMaxR(part: Part): number {
  if (part.prim === "lathe") {
    return part.profile.reduce((m, [r]) => Math.max(m, r), 0);
  }
  if (part.prim === "ball" || part.prim === "blob") return part.r;
  if (part.prim === "box") return Math.max(part.size[0], part.size[2]) / 2;
  if (part.prim === "cone") return part.r;
  if (part.prim === "cylinder") return Math.max(part.rTop, part.rBottom);
  if (part.prim === "tube") return part.axis === "z" ? part.len / 2 : part.r;
  if (part.prim === "arc") return part.r + part.tube;
  if (part.prim === "rod") return part.r;
  if (part.prim === "bone") return Math.max(part.r0, part.r1);
  return 0;
}

/** Vertical extent of a part in its own local space (also used by wear-check). */
export function partYRange(part: Part): [number, number] {
  if (part.prim === "rod" || part.prim === "bone") {
    const r0 = part.prim === "rod" ? part.r : part.r0;
    const r1 = part.prim === "rod" ? part.r : part.r1;
    const ys = [part.from[1] - r0, part.from[1] + r0, part.to[1] - r1, part.to[1] + r1];
    return [Math.min(...ys), Math.max(...ys)];
  }
  const c = part.at[1];
  if (part.prim === "box") return [c - part.size[1] / 2, c + part.size[1] / 2];
  if (part.prim === "ball" || part.prim === "blob") return [c - part.r, c + part.r];
  if (part.prim === "cone" || part.prim === "cylinder") {
    const h = part.prim === "cone" ? part.h : part.h;
    return [c, c + h];
  }
  if (part.prim === "lathe") {
    const ys = part.profile.map(([, y]) => c + y);
    return [Math.min(...ys), Math.max(...ys)];
  }
  if (part.prim === "arc") return [c - part.tube, c + part.r + part.tube];
  if (part.prim === "tube") return [c - part.r, c + part.r];
  return [c, c];
}

/** Covering-dome candidates: lathes tall enough to cup the skull (not flat brim/band). */
function isCoveringLathe(yMin: number, yMax: number): boolean {
  return yMax - yMin > SHORT_LATHE_H;
}

function classify(
  part: Part,
  maxR: number,
  yMin: number,
  yMax: number,
  shellMaxR: number,
): Omit<HeadPartSeat, "index" | "prim" | "color" | "local" | "maxR" | "yMin" | "yMax"> {
  const local = partCenterLocal(part);
  const none = {
    isShell: false,
    isRim: false,
    isBrim: false,
    isNasal: false,
    isOrnament: false,
  };
  // Plume / tall ornament: bone/ball far back or high above shell.
  if (part.prim === "bone" || (part.prim === "ball" && local[1] > 0.12 && maxR < shellMaxR * 0.5)) {
    if (local[2] < -0.05 || local[1] > 0.1) {
      return { ...none, isOrnament: true };
    }
  }
  // Apex rivet ball on top of dome.
  if (part.prim === "ball" && Math.abs(local[0]) < 0.05 && Math.abs(local[2]) < 0.05 && local[1] > 0) {
    return { ...none, isOrnament: true };
  }
  // Front nasal: thin box proud on +z.
  if (part.prim === "box" && local[2] > 0.1 && part.size[0] < 0.1) {
    return { ...none, isNasal: true };
  }
  if (part.prim === "lathe") {
    const h = yMax - yMin;
    // Flat disc / band: wide → brim (sun hat); modest → rim band.
    if (h <= SHORT_LATHE_H && yMin < -0.05) {
      if (maxR >= BRIM_R_MIN) return { ...none, isBrim: true };
      return { ...none, isRim: true };
    }
    // Covering shell ranked among tall lathes only (shellMaxR already excludes brims).
    if (shellMaxR > 0 && maxR >= shellMaxR * 0.85 && isCoveringLathe(yMin, yMax)) {
      return { ...none, isShell: true };
    }
    // Gold band / thin ring on a crown — treat as rim-like, not shell size driver.
    if (h <= 0.1) {
      if (maxR >= BRIM_R_MIN) return { ...none, isBrim: true };
      return { ...none, isRim: true };
    }
  }
  if (part.prim === "cone" && part.repeat?.ring) {
    return { ...none, isOrnament: true };
  }
  return none;
}

/** Seat a headwear parts list against the humanoid crown frame. */
export function seatHeadwearParts(
  parts: Part[],
  opts?: {
    scale?: number;
    build?: BodyBuild;
    fillerId?: string;
    /** Part-doc hidesHair field (omit/true = hide). */
    hidesHair?: boolean;
  },
): HeadSeatReport {
  const frame = humanoidCrownFrame({ scale: opts?.scale, build: opts?.build });
  const fillerId = opts?.fillerId ?? "?";
  const hidesHair = headwearHidesHair(
    opts?.hidesHair === undefined ? {} : { hidesHair: opts.hidesHair },
  );

  // First pass: max r among COVERING lathes only (skip flat brim/band so a
  // straw brim never becomes the shell size driver).
  let coveringMaxR = 0;
  let anyLatheMaxR = 0;
  for (const p of parts) {
    if (p.prim !== "lathe") continue;
    const r = partMaxR(p);
    anyLatheMaxR = Math.max(anyLatheMaxR, r);
    const [yMin, yMax] = partYRange(p);
    if (isCoveringLathe(yMin, yMax)) coveringMaxR = Math.max(coveringMaxR, r);
  }
  // Rank shells against covering lathes; fall back only when the doc has no
  // tall dome (gold-crown band-only) so classification still tags something.
  const shellMaxR = coveringMaxR > 0 ? coveringMaxR : anyLatheMaxR;

  const seats: HeadPartSeat[] = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]!;
    const local = partCenterLocal(p);
    const maxR = partMaxR(p);
    const [yMin, yMax] = partYRange(p);
    const cls = classify(p, maxR, yMin, yMax, shellMaxR || maxR);
    seats.push({
      index: i,
      prim: p.prim,
      color: p.color,
      local,
      maxR,
      yMin,
      yMax,
      ...cls,
    });
  }

  const shellParts = seats.filter((s) => s.isShell);
  const rimParts = seats.filter((s) => s.isRim);
  const brimParts = seats.filter((s) => s.isBrim);
  // Shell radius = tagged shells only. Never fall back to widest lathe (brim).
  // If classification missed a covering lathe, use coveringMaxR as last resort.
  const shellR = shellParts.length
    ? Math.max(...shellParts.map((s) => s.maxR))
    : coveringMaxR > 0
      ? coveringMaxR
      : null;
  const shellRatio = shellR != null ? shellR / frame.headR : null;
  const rimY = rimParts.length
    ? rimParts.reduce((s, p) => s + (p.yMin + p.yMax) / 2, 0) / rimParts.length
    : null;
  const domeBaseY = shellParts.length
    ? Math.min(...shellParts.map((s) => s.yMin))
    : coveringMaxR > 0
      ? Math.min(
        ...seats
          .filter((s) => s.prim === "lathe" && isCoveringLathe(s.yMin, s.yMax))
          .map((s) => s.yMin),
      )
      : null;
  // Apex of covering shell (exclude brim discs + pure ornaments).
  const solid = seats.filter(
    (s) => s.isShell || (s.prim === "lathe" && !s.isBrim && !s.isRim && !s.isOrnament),
  );
  const apexLocalY = solid.length
    ? Math.max(...solid.map((s) => s.yMax))
    : shellParts.length
      ? Math.max(...shellParts.map((s) => s.yMax))
      : null;
  const skullClearance =
    apexLocalY != null ? frame.crown[1] + apexLocalY - frame.skullTopY : null;
  const brimR = brimParts.length ? Math.max(...brimParts.map((s) => s.maxR)) : null;

  const warns: string[] = [];

  if (shellRatio != null) {
    if (shellRatio > SHELL_R_MAX) {
      warns.push(
        `shell r/headR=${shellRatio.toFixed(2)} is oversized (max ${SHELL_R_MAX}) — shrink the covering dome toward skull r≈${frame.headR.toFixed(3)} (wide brims are ignored for this check)`,
      );
    } else if (shellRatio < SHELL_R_MIN) {
      warns.push(
        `shell r/headR=${shellRatio.toFixed(2)} is tighter than the skull (min ${SHELL_R_MIN}) — may clip the egg`,
      );
    }
  }

  // Coverage helmets (hidesHair) must sit at the brow. Crowns/circlets
  // (hidesHair:false) perch on top of the hair — skip rim/dome-float/apex
  // bands that only make sense for full shells.
  if (hidesHair) {
    if (domeBaseY != null && domeBaseY > DOME_BASE_Y_MAX) {
      warns.push(
        `dome base local y=${domeBaseY.toFixed(3)} floats high (want ≤ ${DOME_BASE_Y_MAX}) — lower the shell toward the brow (rim ≈ ${RIM_Y_MIN}…${RIM_Y_MAX})`,
      );
    }

    if (rimY != null && (rimY < RIM_Y_MIN || rimY > RIM_Y_MAX)) {
      warns.push(
        `rim local y≈${rimY.toFixed(3)} outside brow band [${RIM_Y_MIN}, ${RIM_Y_MAX}] — drop or raise the band`,
      );
    }

    if (apexLocalY != null && apexLocalY > APEX_ABOVE_CROWN_MAX) {
      warns.push(
        `shell apex local y=${apexLocalY.toFixed(3)} towers above crown (max ${APEX_ABOVE_CROWN_MAX}) — shorten the dome (plumes are excluded)`,
      );
    }

    if (skullClearance != null && skullClearance > SKULL_CLEARANCE_MAX) {
      warns.push(
        `skull clearance=${skullClearance.toFixed(3)} (shell apex − skull top) is a gap above the head (max ${SKULL_CLEARANCE_MAX}) — drop/shorten the crown so it cups the egg (not a tall floating cylinder)`,
      );
    }
  }

  for (const s of seats.filter((p) => p.isNasal)) {
    // Head surface at crown height is near 0 (skull narrows); use mid-head surface
    // at headY for nasal (brow). face-seat headSurfaceZ pattern: headR * headDepth.
    const surfaceZ = frame.headR * frame.headDepth;
    const outerZ = s.local[2] + (s.maxR > 0 ? s.maxR : 0.02);
    // Nasal local z is crown-relative; face is roughly same xz. outer in "front" space.
    const proud = outerZ - surfaceZ * 0.85; // brow is slightly narrower than equator
    if (proud > NASAL_PROUD_MAX) {
      warns.push(
        `nasal part #${s.index} outer z≈${outerZ.toFixed(3)} floats past the face (proud≈${proud.toFixed(3)}, max ${NASAL_PROUD_MAX}) — pull local z in`,
      );
    }
  }

  return {
    frame,
    fillerId,
    hidesHair,
    parts: seats,
    shellRatio,
    rimY,
    domeBaseY,
    apexLocalY,
    skullClearance,
    brimR,
    warns,
  };
}

/** Pretty table for inspect-model. */
export function formatHeadSeatReport(r: HeadSeatReport): string[] {
  const lines: string[] = [];
  const f = r.frame;
  lines.push(
    `  head-seat  filler=${r.fillerId}  hidesHair=${r.hidesHair}  ` +
      `crownY=${f.crown[1].toFixed(3)}  headR=${f.headR.toFixed(3)}  ` +
      `skullTopY=${f.skullTopY.toFixed(3)}  ` +
      `shellRatio=${r.shellRatio != null ? r.shellRatio.toFixed(2) : "—"}  ` +
      `rimY=${r.rimY != null ? r.rimY.toFixed(3) : "—"}  ` +
      `skullClear=${r.skullClearance != null ? r.skullClearance.toFixed(3) : "—"}` +
      (r.brimR != null ? `  brimR=${r.brimR.toFixed(3)}` : ""),
  );
  lines.push(
    `    ${"#".padStart(2)}  ${"prim".padEnd(8)} ${"color".padEnd(10)} ` +
      `${"loc.y".padStart(7)} ${"maxR".padStart(7)} ${"yMin".padStart(7)} ${"yMax".padStart(7)}  tag`,
  );
  for (const s of r.parts) {
    const tag = s.isShell
      ? "shell"
      : s.isBrim
        ? "brim"
        : s.isRim
          ? "rim"
          : s.isNasal
            ? "nasal"
            : s.isOrnament
              ? "orn"
              : "—";
    lines.push(
      `    ${String(s.index).padStart(2)}  ${s.prim.padEnd(8)} ${s.color.padEnd(10)} ` +
        `${s.local[1].toFixed(3).padStart(7)} ${s.maxR.toFixed(3).padStart(7)} ` +
        `${s.yMin.toFixed(3).padStart(7)} ${s.yMax.toFixed(3).padStart(7)}  ${tag}`,
    );
  }
  for (const w of r.warns) lines.push(`    WARN  ${w}`);
  if (!r.warns.length) lines.push(`    (no head-seat WARNs)`);
  return lines;
}

/**
 * Optional worn-vs-bare width ratio WARN (caller measures AABBs). Soft gate for
 * inspect --compare-bare. Wide sun-hat brims intentionally trip this — seat
 * checks (shellRatio / skullClearance) are the ones that must be clean.
 */
export function warnWornWidthRatio(wornW: number, bareW: number): string | null {
  if (bareW <= 1e-6) return null;
  const ratio = wornW / bareW;
  if (ratio > WORN_WIDTH_RATIO_MAX) {
    return `worn/bare head width ratio=${ratio.toFixed(2)} > ${WORN_WIDTH_RATIO_MAX} — headwear too wide vs bare skull (OK for intentional sun-hat brims; seat checks must still be clean)`;
  }
  return null;
}
